// Disable import errors, as linting occurs for the whole project, and needs to be build agnostic
import i18n from 'i18next';
import type { InitOptions, Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { businessUrl, locales as localesEnums } from '/shared/util/Statics';
import type { WhitelabelConfig } from '/business/shared/state/actions/whitelabel/setWhitelabelConfig';

type LocaleResources = Resource;
type DefaultVariables = Record<string, string>;
type LegacyInitOptions = InitOptions & {
  namespaceSeparator?: string;
};

const init = async (config?: WhitelabelConfig) => {
  let locales: string[];
  let globalDefaultValues: DefaultVariables[];
  if (import.meta.env.NODE_ENV === 'test' || config === undefined) {
    locales = [localesEnums.ENGLISH];
    globalDefaultValues = [
      {
        locale: localesEnums.ENGLISH,
        varWordApplication: 'test_Application',
        varWordBpBusiness: 'test_Castrol',
        varWordCustomerType: 'test_Dealership',
        varWordApplicationPlural: 'test_Applications',
        varWordCustomerTypePlural: 'test_Dealerships',
        varWordApplicationLower: 'test_application',
        varWordBpBusinessLower: 'test_castrol',
        varWordCustomerTypeLower: 'test_dealership',
        varWordApplicationLowerPlural: 'test_applications',
        varWordCustomerTypeLowerPlural: 'test_dealerships',
      },
    ];
  } else {
    const { locales: _locales, defaultVariables: _globalDefaultValues } = config;
    locales = _locales || [localesEnums.ENGLISH];
    globalDefaultValues = _globalDefaultValues || [];
  }

  const resources: LocaleResources = {};

  // Note: at the moment we need to import these long hand to ensure they are included by the parcel bundler.
  // TODO: TECH DEBT: we could write code that loads .json resources like these dynamically over an XMLHttpRequest when
  // a language is selected rather than bundling them all as javascript resources with Parcel. This would be better
  // since only the current language would then be loaded over the network. We then also wouldn't need to maintain
  // this file as resources could be loaded dynamically from a supported list of languages and namespaces.

  if (locales.includes(localesEnums.ENGLISH)) {
    resources[localesEnums.ENGLISH] = {
      questions: await import('/locales/en/_output/questions.json'),
      business: await import('/locales/en/_output/business.json'),
      countries: await import('/locales/en/_output/countries.json'),
      shared: await import('/locales/en/_output/shared.json'),
      initiatives: await import('/locales/en/_output/initiatives.json'),
    };
  }

  if (locales.includes(localesEnums.TEST) && import.meta.env.locale_xx_enabled === 'true') {
    resources[localesEnums.TEST] = {
      questions: await import('/locales/xx/_output/questions.json'),
      business: await import('/locales/xx/_output/business.json'),
      countries: await import('/locales/xx/_output/countries.json'),
      shared: await import('/locales/xx/_output/shared.json'),
      initiatives: await import('/locales/xx/_output/initiatives.json'),
    };
  }

  const initOptions: LegacyInitOptions = {
    ns: ['shared'],
    resources: resources as Resource,
    fallbackLng: 'en',
    appendNamespaceToCIMode: true,
    compatibilityJSON: 'v3' as InitOptions['compatibilityJSON'],
    load: 'languageOnly',
    debug: false,
    defaultNS: 'default',
    keySeparator: '.',
    namespaceSeparator: ':',
    parseMissingKeyHandler: () => '___',
    interpolation: {
      escapeValue: false,
    },
  };

  await i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .init(initOptions as InitOptions);

  // Ensure locale is 'safe' as in, we will always match from the relevant globalDefaultValues
  const loadedLocale = !locales.includes(i18n.language) ? localesEnums.ENGLISH : i18n.language;

  // Get the defaultValues for the current language
  const defaultValues = globalDefaultValues.find((defaultPropItem: DefaultVariables) => {
    return defaultPropItem.locale === loadedLocale;
  });
  if (defaultValues) {
    // We need to inject into defaultValues into the i18n object after initialization, as prior to i18n.init,
    // we cannot reliably get access to i18n.language
    i18n.options.interpolation = i18n.options.interpolation || {};
    i18n.options.interpolation.defaultVariables = {
      ...defaultValues,
      sharedFrontendURL: businessUrl,
    };
  }
};

export { init, i18n };
